import _ from "lodash";
/** external */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  DeleteOutlined,
  DotsVerticalOutlined,
  InformationOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";
import {
  Avatar,
  Button,
  Checkbox,
  Dropdown,
  DropdownMenu,
  RadioButton,
  TextInput,
  Tooltip,
} from "@toddle-edu/ds-web";
import classNames from "classnames";

/** internal */
import { useI18n } from "Hooks";
import { resolveIconByName } from "Utils/IconRegistry";

/** relative */
import classes from "./GenericCard.scss";
import { getIconList } from "UIComponents/GenericTableEditor/IconPicker/IconPickerUtils";
import {
  getColorNameFromColorVariable,
  getIconKind,
} from "Datasets/modules/Utils";

const getDropdownOptions = t => {
  return [
    {
      label: t("common:edit"),
      key: "EDIT",
      icon: <PencilLineOutlined />,
    },
    {
      label: t("common:delete"),
      key: "DELETE",
      icon: <DeleteOutlined />,
      isDestructive: true,
    },
  ];
};

const styles = {
  textInput: {
    padding: "0px",
    background: "transparent",
    width: "100%",
    height: "20px",
  },
  tooltipOverlayStyle: {
    maxWidth: "320px",
  },
};

const ICON_LIST = getIconList({ size: "medium" });

const GenericCard = props => {
  const {
    disabled,
    isFocused,
    isUpdateActionsAllowed,
    isSelected,
    onCardUpdate,
    item,
    allowMultiSelect,
    onBlurCardInputField,
    onClickEdit,
    showSubtextUnderInfo,
  } = props;
  const { id, label, subText, icon, placeholder } = item;

  const { iconName, colorVariable } = _.get(icon, "metadata", {});
  const { isEmoji } = getIconKind(icon);
  const isStaticIcon = !!iconName;
  const emoji = isEmoji ? _.get(icon, "title") : undefined;
  const avatarImage = _.get(icon, "url", icon);

  const hasAvatarOrIcon = !!avatarImage || isStaticIcon || isEmoji;

  const isCheckboxDisabled =
    _.isEmpty(_.get(item, "value", "")) && _.isEmpty(_.get(item, "label", ""));

  const infoSubText = showSubtextUnderInfo ? subText : null;
  const showSubText = !showSubtextUnderInfo && subText;

  const hasImageAndSubText = hasAvatarOrIcon && showSubText;

  const itemId = id;

  const { t } = useI18n(["common"]);

  const inputRef = useRef();
  const [updatedLabel, setUpdatedLabel] = useState(label);

  // Debounced function to call onCardUpdate
  const debouncedOnCardUpdate = useCallback(
    _.debounce((cardId, updatedLabel) => {
      onCardUpdate({
        cardId,
        updatedLabel,
        actionType: "UPDATE_TITLE",
      });
    }, 300),
    [onCardUpdate]
  );

  const handleInputFocus = useCallback(e => {
    e.target.select();
  }, []);

  const handleLabelChange = useCallback(
    e => {
      const newValue = e.target.value;
      setUpdatedLabel(newValue);
      debouncedOnCardUpdate(itemId, newValue);
    },
    [itemId, debouncedOnCardUpdate]
  );

  const onInfoIconClick = e => {
    e?.stopPropagation();
  };

  useEffect(() => {
    if (isFocused) {
      inputRef.current.focus();
    }
  }, [isFocused]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedOnCardUpdate.cancel();
    };
  }, [debouncedOnCardUpdate]);

  const onDropdownItemClick = e => {
    const { key, domEvent } = e;
    domEvent.stopPropagation();
    switch (key) {
      case "EDIT":
        onClickEdit({ cardId: itemId });
        break;
      case "DELETE":
        onCardUpdate({ cardId: itemId, actionType: key });
        break;
    }
  };

  const handleOnCardClick = e => {
    e.stopPropagation();
    onCardUpdate({
      cardId: itemId,
      actionType: isSelected && allowMultiSelect ? "DESELECT" : "SELECT",
    });
  };

  const containerClasses = classNames(classes.container, {
    [classes.selectedContainerBorder]: isSelected,
  });

  const leftContainerClasses = classNames(classes.leftContainer, {
    [classes.verticalCenter]: hasAvatarOrIcon && !hasImageAndSubText,
  });

  const rightButtonContainerClasses = classNames(classes.rightButtonContainer, {
    [classes.verticalCenter]: hasAvatarOrIcon && !hasImageAndSubText,
  });

  const labelInfoContainerClasses = classNames(classes.labelInfoContainer, {
    [classes.inputContainer]: isFocused,
  });

  const topContainerClasses = classNames(classes.topContainer, {
    [classes.verticalCenter]: hasAvatarOrIcon && !hasImageAndSubText,
  });

  const contentContainerClasses = classNames(classes.contentContainer, {
    [classes.topMargin]: hasImageAndSubText,
  });

  const handleClick = e => {
    if (isCheckboxDisabled || disabled) return;

    handleOnCardClick(e);
  };

  const getContent = () => {
    return (
      <div className={contentContainerClasses}>
        <div className={labelInfoContainerClasses}>
          {isFocused ? (
            <TextInput
              value={updatedLabel}
              placeholder={placeholder || t("common:enter_text")}
              onChange={handleLabelChange}
              variant="plain"
              dsVersion="2.0"
              style={styles.textInput}
              onBlur={onBlurCardInputField}
              ref={inputRef}
              width={"100%"}
              onFocus={handleInputFocus}
            />
          ) : (
            <span className={"text-label"}>{label}</span>
          )}
          {infoSubText ? (
            <div className={classes.infoIconContainer}>
              <Tooltip
                tooltip={infoSubText}
                dsVersion="2.0"
                placement="bottom"
                showArrow={true}
                overlayInnerStyle={styles.tooltipOverlayStyle}
              >
                <InformationOutlined
                  variant="subtle"
                  size="xxx-small"
                  onClick={onInfoIconClick}
                />
              </Tooltip>
            </div>
          ) : null}
        </div>
        {showSubText ? (
          <span className={classNames("text-body-s", classes.subText)}>
            {subText}
          </span>
        ) : null}
      </div>
    );
  };

  const renderIcon = () => {
    let iconComponent;
    if (isStaticIcon) {
      iconComponent = resolveIconByName(iconName, ICON_LIST);
    }
    const { isDuotone, element } = iconComponent || {};
    if (isDuotone) return element;
    const iconElement = element;
    if (iconElement)
      return (
        <Avatar
          iconType="icon"
          size="x-large"
          shape="square"
          theme={"light"}
          dsVersion="2.0"
          icon={iconElement}
          color={getColorNameFromColorVariable(colorVariable)}
        />
      );

    if (avatarImage || isEmoji)
      return (
        <Avatar
          shape="square"
          name={label}
          dsVersion="2.0"
          size="x-large"
          color="violet"
          src={avatarImage}
        >
          {isEmoji ? <span>{emoji}</span> : null}
        </Avatar>
      );
  };

  return (
    <div className={containerClasses} onClick={handleClick}>
      <div className={topContainerClasses}>
        <div className={leftContainerClasses}>
          {renderIcon()}
          {!hasImageAndSubText ? getContent() : null}
        </div>
        <div className={rightButtonContainerClasses}>
          {isUpdateActionsAllowed ? (
            <div className={classes.dropdownIcon}>
              <Dropdown
                overlay={
                  <DropdownMenu
                    options={getDropdownOptions(t)}
                    onClick={onDropdownItemClick}
                    dsVersion="2.0"
                  />
                }
              >
                <Button
                  isCompact={true}
                  dsVersion="2.0"
                  type="inline"
                  onClick={e => e.stopPropagation()}
                >
                  <DotsVerticalOutlined variant="subtle" />
                </Button>
              </Dropdown>
            </div>
          ) : null}
          <div>
            {allowMultiSelect ? (
              <Checkbox
                dsVersion="2.0"
                variant="subtle"
                isChecked={isSelected}
                onClick={handleOnCardClick}
                disabled={disabled || isCheckboxDisabled}
              />
            ) : (
              <RadioButton
                size="medium"
                dsVersion="2.0"
                checked={isSelected}
                shouldStopPropagation
                onChange={handleOnCardClick}
                disabled={disabled}
              />
            )}
          </div>
        </div>
      </div>
      {hasImageAndSubText ? getContent() : null}
    </div>
  );
};

export default GenericCard;
